import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { router } from '@inertiajs/react'
import { Trash2, Plus, CheckCircle, Search, ArrowUpDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useState } from 'react'

interface User {
  id: number
  name: string
  email: string
  phone: string
  role: string
  verified: boolean
  created_at: string
}

interface School {
  id: number
  name: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  role: string
  verified?: string
  search?: string
  sort_by: string
  sort_direction: string
}

interface UsersIndexProps {
  users: User[]
  school: School
  pagination: Pagination
  filters: Filters
}

export default function UsersIndex({ users, school, pagination, filters }: UsersIndexProps) {
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null)
  const [deleteUserEmail, setDeleteUserEmail] = useState<string>('')
  const [verifyUserId, setVerifyUserId] = useState<number | null>(null)
  const [verifyUserName, setVerifyUserName] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState(filters.search || '')

  const handleFilter = (key: string, value: string) => {
    router.get('/users', {
      ...filters,
      [key]: value,
      page: 1 // Reset to first page on filter change
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/users', {
      ...filters,
      search: searchTerm,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    router.get('/users', {
      ...filters,
      search: '',
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleSort = (column: string) => {
    const newDirection = filters.sort_by === column && filters.sort_direction === 'asc' ? 'desc' : 'asc'
    router.get('/users', {
      ...filters,
      sort_by: column,
      sort_direction: newDirection
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handlePageChange = (page: number) => {
    router.get('/users', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleDelete = (userId: number, email: string) => {
    setDeleteUserId(userId)
    setDeleteUserEmail(email)
  }

  const confirmDelete = () => {
    if (deleteUserId) {
      router.delete(`/users/${deleteUserId}`)
      setDeleteUserId(null)
      setDeleteUserEmail('')
    }
  }

  const handleVerify = (userId: number, name: string) => {
    setVerifyUserId(userId)
    setVerifyUserName(name)
  }

  const confirmVerify = () => {
    if (verifyUserId) {
      router.patch(`/users/${verifyUserId}/verify`)
      setVerifyUserId(null)
      setVerifyUserName('')
    }
  }

  const getRoleBadge = (role: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      owner: 'default',
      manager: 'secondary',
      teacher: 'outline',
      student: 'outline',
    }

    const labels: Record<string, string> = {
      owner: 'Dono',
      manager: 'Gerente',
      teacher: 'Professor',
      student: 'Aluno',
    }

    return (
      <Badge variant={variants[role] || 'outline'}>
        {labels[role] || role}
      </Badge>
    )
  }

  // Loading skeleton
  if (!users || !school) {
    return (
      <DashboardLayout schoolName="Carregando...">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <Skeleton className="h-9 w-[200px]" />
              <Skeleton className="h-5 w-[350px] mt-2" />
            </div>
            <Skeleton className="h-10 w-[180px]" />
          </div>

          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-[180px]" />
              <Skeleton className="h-4 w-[250px] mt-2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex-1">
                      <Skeleton className="h-5 w-[250px]" />
                    </div>
                    <Skeleton className="h-5 w-[80px] ml-4" />
                    <Skeleton className="h-5 w-[100px] ml-4" />
                    <Skeleton className="h-5 w-[120px] ml-4" />
                    <Skeleton className="h-8 w-[80px] ml-4" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout schoolName={school?.name}>
      <div className="p-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Usuários</h2>
            <p className="text-muted-foreground">
              Total de {pagination.total_count} usuários cadastrados
            </p>
          </div>
          <Button onClick={() => router.visit('/users/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Adicionar Usuário
          </Button>
        </div>

        {/* Card Único com Filtros e Tabela */}
        <Card>
          <CardHeader>
            <CardTitle>Usuários</CardTitle>
            <CardDescription>
              Exibindo {users.length} de {pagination.total_count} usuários cadastrados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Filtros e Busca */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Busca */}
                <form onSubmit={handleSearch} className="col-span-12 md:col-span-6">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        placeholder="Buscar por nome ou email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleSearch(e as any)
                          }
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={handleClearSearch}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Button type="submit" variant="secondary">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </form>

                {/* Filtro por Role */}
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground font-medium whitespace-nowrap">Função:</label>
                    <Select value={filters.role} onValueChange={(value) => handleFilter('role', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos os papéis" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="student">Alunos</SelectItem>
                        <SelectItem value="teacher">Professores</SelectItem>
                        <SelectItem value="manager">Gerentes</SelectItem>
                        <SelectItem value="owner">Donos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Filtro por Status */}
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground font-medium whitespace-nowrap">Status:</label>
                    <Select value={filters.verified || 'all'} onValueChange={(value) => handleFilter('verified', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos os status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="true">Verificados</SelectItem>
                        <SelectItem value="false">Pendentes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Mostrar filtros ativos de forma explícita */}
              {(filters.search || filters.role !== 'all' || filters.verified) && (
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded p-2">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold text-blue-900 dark:text-blue-100">
                      Filtros Aplicados
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => router.get('/users', {}, { preserveState: true })}
                      className="h-6 px-2 text-xs"
                    >
                      Limpar
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {filters.search && (
                      <Badge variant="secondary" className="gap-1 pr-0 text-xs">
                        <span>🔍 "{filters.search}"</span>
                        <button
                          onClick={handleClearSearch}
                          className="ml-1 hover:opacity-70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    {filters.role !== 'all' && (
                      <Badge variant="secondary" className="gap-1 pr-0 text-xs">
                        <span>
                          👤 {filters.role === 'student' ? 'Alunos' : filters.role === 'teacher' ? 'Professores' : filters.role === 'manager' ? 'Gerentes' : 'Donos'}
                        </span>
                        <button
                          onClick={() => handleFilter('role', 'all')}
                          className="ml-1 hover:opacity-70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    {filters.verified && (
                      <Badge variant="secondary" className="gap-1 pr-0 text-xs">
                        <span>
                          ✓ {filters.verified === 'true' ? 'Verificados' : 'Pendentes'}
                        </span>
                        <button
                          onClick={() => handleFilter('verified', '')}
                          className="ml-1 hover:opacity-70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tabela de Usuários */}
            <div className="border-t pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8"
                      onClick={() => handleSort('name')}
                    >
                      Nome
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8"
                      onClick={() => handleSort('email')}
                    >
                      Email
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8"
                      onClick={() => handleSort('role')}
                    >
                      Função
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8"
                      onClick={() => handleSort('verified')}
                    >
                      Status
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      {user.verified ? (
                        <Badge variant="outline" className="text-green-600 dark:text-green-400">
                          Verificado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-yellow-600 dark:text-yellow-400">
                          Pendente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => router.visit(`/users/${user.id}`)}
                        >
                          Ver Perfil
                        </Button>
                        {!user.verified && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleVerify(user.id, user.name)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Verificar
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Verificar Usuário</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja verificar o usuário <strong>{verifyUserName}</strong>? 
                                  Isso permitirá acesso completo ao sistema.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => {
                                  setVerifyUserId(null)
                                  setVerifyUserName('')
                                }}>
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={confirmVerify}>
                                  Verificar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {user.role !== 'owner' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user.id, user.email)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja deletar o usuário <strong>{deleteUserEmail}</strong>? 
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => {
                                  setDeleteUserId(null)
                                  setDeleteUserEmail('')
                                }}>
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Deletar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Paginação */}
            <div className="flex items-center justify-between px-2 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                Página {pagination.current_page} de {pagination.total_pages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                
                {/* Números de página */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNumber
                    if (pagination.total_pages <= 5) {
                      pageNumber = i + 1
                    } else if (pagination.current_page <= 3) {
                      pageNumber = i + 1
                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                      pageNumber = pagination.total_pages - 4 + i
                    } else {
                      pageNumber = pagination.current_page - 2 + i
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={pagination.current_page === pageNumber ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    )
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
